/**
 * API client that works in both React Native and web environments.
 */

import type { ErrorResponse } from '../types';
import { isTauri } from '../utils/env';
import { createRequestId } from '../utils/requestId';

/** One origin per browser tab / native app launch; deliberately not persisted. */
export const clientOrigin = createRequestId();

/**
 * Default per-request timeout (AB#423), raised 20s -> 29s under AB#635.
 * Exported so the self-check drives its fake clock from the same number
 * instead of a literal that silently rots when this is retuned.
 */
export const DEFAULT_TIMEOUT_MS = 29_000;

/** Configurable API base URL - can be set for React Native */
let configuredApiBase: string | null = null;

/** Set the API base URL (for React Native or custom configurations) */
export function setApiBase(url: string): void {
  configuredApiBase = url;
}

/** Determine API base URL based on environment (exported for the AB#425 env self-check) */
export const getApiBase = (): string => {
  // Use configured URL if set (for React Native)
  if (configuredApiBase) {
    return configuredApiBase;
  }
  // In browser (web admin), use relative path - will be proxied by Vite
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    return '/api';
  }
  // Default fallback
  return 'http://localhost:5150/api';
};

/**
 * AB#497: a release native (React Native) build must never talk to a loopback
 * host. If it does, the env/channel resolution regressed and every request would
 * fail instantly with zero external trace (a silent dead-loopback) — the exact
 * Android login failure that had no user-facing reason. Detect it and surface a
 * loud, specific error instead. Inert on web (`document` defined) and in dev RN
 * builds (`__DEV__ === true`), where a loopback base is legitimate.
 */
function isReleaseNativeLoopback(url: string): boolean {
  const g = globalThis as { __DEV__?: boolean; document?: unknown };
  const isReleaseNative = typeof g.document === 'undefined' && g.__DEV__ === false;
  return (
    isReleaseNative &&
    /\/\/(?:localhost|127\.0\.0\.1|10\.0\.2\.2)(?::\d+)?(?:\/|$)/.test(url)
  );
}

/**
 * AB#432: media `item.url` is root-relative (`/media/...`). In the desktop
 * (Tauri) wrap the webview origin is `tauri://localhost`, not the API host, so
 * `<img src="/media/...">` resolves against the wrong origin and the thumbnail
 * breaks. Prefix the API origin when the configured base is absolute (desktop);
 * on web the base is relative (`/api`) so a root-relative URL already resolves
 * same-origin — return it untouched. DISPLAY ONLY: never persist the resolved
 * URL into content, or the env host leaks into published pages.
 */
export function resolveMediaUrl(url: string): string {
  if (!url.startsWith('/')) return url;
  const base = getApiBase();
  if (!/^https?:\/\//i.test(base)) return url; // web: relative base → same-origin
  return new URL(base).origin + url;
}

/** API request options */
export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Public token-verification requests must not send or invalidate the current session. */
  auth?: 'session' | 'none';
}

/** API error with status and response data */
export class ApiError extends Error {
  constructor(
    public status: number,
    public data: ErrorResponse,
  ) {
    super(data.error || `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

/** Token getter function type */
export type TokenGetter = () => string | null;

/** Global token getter - can be set by auth store */
let globalTokenGetter: TokenGetter = () => null;

/** Set the global token getter */
export function setTokenGetter(getter: TokenGetter): void {
  globalTokenGetter = getter;
}

/** Callback invoked when the API returns 401 (token expired / invalid) */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

/** Register a handler that fires on any 401 API response (e.g. clear auth + redirect) */
export function setOnUnauthorized(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

/**
 * Make an API request.
 *
 * @param endpoint - API endpoint (e.g., "/content/posts")
 * @param options - Request options
 * @returns Promise resolving to response data
 * @throws ApiError if response is not ok
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers = {}, signal, auth = 'session' } = options;
  const apiBase = getApiBase();

  // AB#497: fail loud, not silent, if a shipped build resolved to a loopback base.
  if (isReleaseNativeLoopback(apiBase)) {
    throw new ApiError(0, {
      error:
        `App misconfigured: API base "${apiBase}" is a local loopback address, so ` +
        `this build can't reach the server. The EAS channel/env wasn't applied to ` +
        `this bundle.`,
    });
  }

  // Get auth token
  const token = auth === 'session' ? globalTokenGetter() : null;

  // FormData bodies (file uploads) must not be JSON-stringified, and the
  // browser must set the multipart Content-Type (with boundary) itself.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const requestHeaders: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(auth === 'session' ? { 'X-Wpp-Client': clientOrigin } : {}),
    ...headers,
  };

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Desktop (Tauri): route through the native-HTTP plugin so webview CORS
  // never applies (server CORS allowlist stays as belt-and-braces). plugin-http
  // v2's fetch is WHATWG-compatible incl. FormData bodies + AbortSignal. The
  // dynamic import keeps the plugin out of the web bundle (same pattern as
  // openExternal in ../utils/env).
  const doFetch = isTauri() ? (await import('@tauri-apps/plugin-http')).fetch : fetch;

  // Default timeout when the caller gave us no signal of their own — RN's
  // fetch has no built-in timeout so a stalled radio would hang a queryFn
  // forever. Caller-supplied signal wins (no second controller, no double
  // abort). plugin-http v2's fetch honors AbortSignal like WHATWG fetch does.
  //
  // AB#635: raised 20s -> 29s (Rick's call). Once desktop file picks stopped
  // sending 0-byte bodies, real multi-MB uploads started actually transferring
  // and a slow link could exceed the old ceiling — the upload died at exactly
  // 20s with "Request timed out". This budget is shared by every request, so
  // the ONE knob lives here and the message reads from it (they used to be two
  // literals that could drift apart).
  // ponytail: a blanket bump is the small fix; the targeted one is a longer
  // (or absent) timeout on the media-upload path only, since that is the one
  // caller whose duration scales with payload size rather than server latency.
  const timeoutController = signal ? null : new AbortController();
  let timedOut = false;
  const timer = timeoutController
    ? setTimeout(() => {
        timedOut = true;
        timeoutController.abort();
      }, DEFAULT_TIMEOUT_MS)
    : null;

  let response: Awaited<ReturnType<typeof doFetch>>;
  try {
    response = await doFetch(`${apiBase}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      signal: signal ?? timeoutController!.signal,
    });
  } catch (err) {
    if (timedOut) {
      throw new ApiError(408, {
        error: `Request timed out after ${DEFAULT_TIMEOUT_MS / 1000}s: ${endpoint}`,
      });
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }

  // Handle no-content responses
  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json().catch(() => ({
    error: `HTTP ${response.status}`,
  }));

  if (!response.ok) {
    if (response.status === 401 && auth === 'session' && onUnauthorized) {
      onUnauthorized();
    }

    // Handle ApiResponse error envelope: { success: false, error: { code, message } }
    if (data?.error?.code && data?.error?.message) {
      throw new ApiError(response.status, {
        error: data.error.message,
        code: data.error.code,
        details: data.error.details,
      });
    }
    // Fallback for plain error responses
    throw new ApiError(response.status, data as ErrorResponse);
  }

  return data as T;
}

/**
 * Build the ApiError for a non-2xx response, honouring both the
 * `{ success:false, error:{ code, message } }` envelope and plain `{ error }`.
 * ponytail: only apiStream uses it — apiRequest/apiRequestBinary keep their
 * inline copies so this addition can't regress the paths every screen uses.
 */
async function errorFromResponse(response: {
  status: number;
  json: () => Promise<unknown>;
}): Promise<ApiError> {
  if (response.status === 401 && onUnauthorized) {
    onUnauthorized();
  }
  const data = (await response
    .json()
    .catch(() => ({ error: `HTTP ${response.status}` }))) as {
    error?: string | { code?: string; message?: string; details?: Record<string, string> };
  };
  const envelope = typeof data?.error === 'object' ? data.error : null;
  if (envelope?.code && envelope?.message) {
    return new ApiError(response.status, {
      error: envelope.message,
      code: envelope.code,
      details: envelope.details,
    });
  }
  return new ApiError(response.status, data as ErrorResponse);
}

/**
 * Request returning the raw streaming `Response` (SSE), for Baxter POST turns
 * and the live-events GET. Same auth header, same base
 * URL, and the same native-HTTP transport on desktop as `apiRequest`
 * (plugin-http v2 gives a real `ReadableStream` body, so `res.body.getReader()`
 * works there too). Deliberately NO timeout: a chat turn legitimately runs
 * longer than DEFAULT_TIMEOUT_MS; the caller's AbortSignal is the only stop.
 * Pre-stream failures are plain JSON, so they throw the exact ApiError shape
 * `parseUpgradeRequired` (402) and the 503 `ai_not_configured` check expect.
 */
export async function apiStream(
  endpoint: string,
  body: unknown,
  signal?: AbortSignal,
  streamFetch?: typeof fetch,
  method: 'POST' | 'GET' = 'POST',
): Promise<Response> {
  const token = globalTokenGetter();
  const doFetch = streamFetch ?? (isTauri() ? (await import('@tauri-apps/plugin-http')).fetch : fetch);

  const response = await doFetch(`${getApiBase()}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-Wpp-Client': clientOrigin,
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: method === 'GET' ? undefined : JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  return response as Response;
}

/**
 * Make an authenticated API request that returns raw bytes (file downloads).
 * Same auth + native-transport handling as apiRequest, no JSON envelope.
 */
/** Exact trusted media URL, or null. Cache busters are allowed; credential/redirect query parameters are not. */
export function authenticatedMediaUrl(value: string): string | null {
  if (!value || /[\\\x00-\x20\x7f]/.test(value)) return null;
  try {
    const location = typeof window !== 'undefined' ? window.location?.origin : undefined;
    const base = new URL(getApiBase(), location);
    if (!['http:', 'https:'].includes(base.protocol)) return null;
    const url = new URL(value, base.origin);
    if (url.origin !== base.origin || url.username || url.password || !url.pathname.startsWith('/media/')) return null;
    const path = decodeURIComponent(url.pathname);
    if (/[\\%\x00-\x1f\x7f]/.test(path) || path.split('/').some((part) => part === '.' || part === '..')) return null;
    for (const [key, value] of url.searchParams) if (!['t', 'v', 'wpp_resize'].includes(key) || !(key === 'wpp_resize' ? /^\d+$/ : /^[a-zA-Z0-9._-]+$/).test(value)) return null;
    url.hash = '';
    return url.href;
  } catch { return null; }
}

/** Native image/download callers must use this allowlist, never copy a bearer to arbitrary URLs. */
export function mediaRequestHeaders(url: string): Record<string, string> {
  const trusted = authenticatedMediaUrl(url);
  const token = trusted && !isReleaseNativeLoopback(trusted) ? globalTokenGetter() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function binaryResponse(url: string, signal?: AbortSignal, media = false): Promise<Response> {
  const token = globalTokenGetter();
  const doFetch = isTauri() ? (await import('@tauri-apps/plugin-http')).fetch : fetch;

  const response = await doFetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
    ...(media ? { redirect: 'error' as const, maxRedirections: 0, credentials: 'omit' as const } : {}),
  });

  if (media && (response.redirected || (response.status >= 300 && response.status < 400) || (response.url && response.url !== url))) {
    throw new ApiError(403, { error: 'Media redirects are not allowed' });
  }

  if (!response.ok) {
    if (response.status === 401 && onUnauthorized) {
      onUnauthorized();
    }
    const data = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    if (data?.error?.code && data?.error?.message) {
      throw new ApiError(response.status, {
        error: data.error.message,
        code: data.error.code,
        details: data.error.details,
      });
    }
    throw new ApiError(response.status, data as ErrorResponse);
  }

  return response as Response;
}

export async function apiRequestBinary(endpoint: string, signal?: AbortSignal): Promise<Uint8Array> {
  return new Uint8Array(await (await binaryResponse(`${getApiBase()}${endpoint}`, signal)).arrayBuffer());
}

/** Display/download bytes only; callers retain the original URL in persisted content. */
export async function fetchMediaBytes(value: string, signal?: AbortSignal): Promise<{ bytes: Uint8Array; contentType: string }> {
  const url = authenticatedMediaUrl(value);
  if (!url || isReleaseNativeLoopback(url)) throw new ApiError(403, { error: 'Not an authorized media URL' });
  const response = await binaryResponse(url, signal, true);
  return { bytes: new Uint8Array(await response.arrayBuffer()), contentType: response.headers.get('content-type') || 'application/octet-stream' };
}

/** Convenience methods */
export const api = {
  get: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'PUT', body }),

  patch: <T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE', body }),
};
